import { useContext, type ReactNode } from 'react';
import { PackageContext } from '@/contexts/PackageContext';
import { useEntitlement } from '@/hooks/useEntitlement';
import { usePackagePending } from '@/hooks/useLockedEntry';
import { LockedFeatureCard } from '@/components/gating/LockedFeatureCard';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { EntitlementFeatureKey } from '@/types/package';

interface RequireEntitlementProps {
  /** Registry key of the feature the whole page belongs to, e.g. 'wordpress' */
  feature: EntitlementFeatureKey;
  /** The page's own PageHeader, shown above the spinner and the locked card so locked/unlocked headers match */
  header?: ReactNode;
  children: ReactNode;
}

/**
 * Route-level guard for gated feature pages: a DIRECT URL must show the locked card, not only lose its nav entry.
 * Decision comes from useEntitlement, so mode-off behaviour is unchanged (new keys are never locked before enforce;
 * legacy keys follow their old logic). While the package is loading nothing is decided (no locked flash), and
 * when the entitlements resolver failed the page is left alone (the UI locks nothing without a payload).
 */
export const RequireEntitlement = ({ feature, header, children }: RequireEntitlementProps) => {
  const context = useContext(PackageContext);
  const { locked, upsellPlanSlug } = useEntitlement(feature);
  const pending = usePackagePending();

  if (pending) {
    return (
      <div>
        {header}
        <div className="p-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }
  if (!context?.entitlements || !locked) return <>{children}</>;

  return (
    <div>
      {header}
      <div className="mt-6 sm:mt-0 max-w-2xl mx-auto">
        <LockedFeatureCard feature={feature} upsellPlanSlug={upsellPlanSlug} />
      </div>
    </div>
  );
};

export default RequireEntitlement;
